import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DefaultSEO from '../components/DefaultSEO';
import { offerService } from '../services/offerService';
import { Loader2, ArrowLeft, Tag, Calendar, ShieldCheck, ChevronRight } from 'lucide-react';

const OfferDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [offer, setOffer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1';
    const storageBase = apiBase.replace(/\/api\/v1\/?$/, '') + '/storage';

    const handleCopy = () => {
        if (!offer) return;
        navigator.clipboard.writeText(offer.code?.toUpperCase() || '');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    useEffect(() => {
        const fetchOfferDetails = async () => {
            setLoading(true);
            try {
                // Fetching all offers and filtering since there might not be a direct by-id endpoint yet
                const data = await offerService.getOffers();
                const allOffers = data.data.data || [];
                const foundOffer = allOffers.find((o) => o.id.toString() === id);
                setOffer(foundOffer);
            } catch (err) {
                console.error("Error fetching offer details:", err);
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchOfferDetails();
    }, [id]);

    if (loading) {
        return (
            <div className="container py-5 d-flex justify-content-center align-items-center min-vh-100">
                <Loader2 className="animate-spin text-primary" size={48} />
            </div>
        );
    }

    if (!offer) {
        return (
            <div className="container py-5 text-center min-vh-100">
                <div className="py-5 bg-light rounded-4 mt-5">
                    <h2 className="fw-bold text-dark mb-3">Offer Not Found</h2>
                    <p className="text-muted mb-4">This offer may have expired or does not exist.</p>
                    <button onClick={() => navigate('/offers')} className="btn btn-primary rounded-pill px-4">
                        View All Offers
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <DefaultSEO title={`${offer.title} | ZoyoTrip`} />

            {/* Header Banner */}
            <div className="position-relative bg-dark text-white" style={{ height: '400px' }}>
                <img
                    src={offer.image ? `${storageBase}/${offer.image}` : "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1400' height='400' viewBox='0 0 1400 400'><rect width='100%' height='100%' fill='%23e5e7eb'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='48' font-weight='bold' fill='%239ca3af'>Offer</text></svg>"}
                    alt={offer.title}
                    className="w-100 h-100 object-fit-cover opacity-50"
                />
                <div className="position-absolute top-0 start-0 w-100 h-100 p-4 p-md-5 d-flex flex-column justify-content-center align-items-center text-center get-in-touch-gradient">
                    {offer.discount_value && (
                        <div className="position-absolute top-0 start-0 m-3">
                            <span className="badge bg-danger rounded-pill px-3 py-2 shadow-sm fs-6">
                                {offer.discount_type === 'percentage' ? `${parseFloat(offer.discount_value)}%` : `₹${parseFloat(offer.discount_value)}`} OFF
                            </span>
                        </div>
                    )}
                    <h1 className="fw-bold display-4 mb-3 text-white text-shadow-sm">{offer.title}</h1>
                    <p className="lead text-white-50 max-w-2xl text-shadow-sm">Claim this exclusive offer and make your next journey unforgettable.</p>
                </div>
            </div>

            {/* Content Section */}
            <div className="container py-5">
                <div className="row justify-content-center">
                    <div className="col-lg-8">
                        {/* Interactive Code Container */}
                        <div className="card border-0 shadow-sm rounded-4 mb-5 transform-up">
                            <div className="card-body p-4 p-md-5 text-center bg-primary bg-gradient text-white rounded-4">
                                <h4 className="fw-bold mb-4">Your Promo Code</h4>
                                <div 
                                    className="bg-white rounded-3 p-4 d-inline-block shadow-sm cursor-pointer" 
                                    onClick={handleCopy}
                                    style={{ cursor: 'pointer' }}
                                    title="Click to copy"
                                >
                                    <span className="font-monospace fs-2 fw-bold text-primary px-3 tracking-wider">
                                        {offer.code?.toUpperCase()}
                                    </span>
                                </div>
                                <p className={`mt-4 mb-0 fw-bold ${copied ? 'text-white' : 'opacity-75'}`}>
                                    {copied ? '✓ Code copied to clipboard!' : 'Apply this code at checkout to claim your discount.'}
                                </p>
                            </div>
                        </div>

                        {/* Offer Details Grid */}
                        <div className="row g-4 mb-5">
                            <div className="col-md-4">
                                <div className="p-4 bg-light rounded-4 h-100 border border-gray-100 text-center hover-shadow transition-all">
                                    <Tag className="text-primary mb-3 mx-auto" size={32} />
                                    <h5 className="fw-bold text-dark">Valid On</h5>
                                    <p className="text-muted small mb-0">Select properties and premium bookings</p>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="p-4 bg-light rounded-4 h-100 border border-gray-100 text-center hover-shadow transition-all">
                                    <Calendar className="text-primary mb-3 mx-auto" size={32} />
                                    <h5 className="fw-bold text-dark">Duration</h5>
                                    <p className="text-muted small mb-0">Limited time offer. Terms apply.</p>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="p-4 bg-light rounded-4 h-100 border border-gray-100 text-center hover-shadow transition-all">
                                    <ShieldCheck className="text-primary mb-3 mx-auto" size={32} />
                                    <h5 className="fw-bold text-dark">Verified</h5>
                                    <p className="text-muted small mb-0">Official ZoyoTrip Promotion</p>
                                </div>
                            </div>
                        </div>

                        <div className="text-center mt-5">
                            <button
                                onClick={() => navigate('/search')}
                                className="btn btn-primary btn-lg rounded-pill px-5 py-3 fw-bold shadow-lg hover-scale d-inline-flex align-items-center justify-content-center"
                            >
                                Book Now using Offer <ChevronRight className="ms-2" size={20} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                .text-shadow-sm { text-shadow: 0 2px 4px rgba(0,0,0,0.5); }
                .get-in-touch-gradient { background: linear-gradient(0deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 100%); }
                .tracking-wider { letter-spacing: 0.1em; }
                .transform-up { margin-top: -80px; position: relative; z-index: 10; }
                .hover-shadow:hover { box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important; transform: translateY(-3px); }
                .transition-all { transition: all .3s ease; }
                .hover-scale { transition: transform .2s; }
                .hover-scale:hover { transform: scale(1.03); }
            `}} />
        </>
    );
};

export default OfferDetailsPage;
